/** Match RN `createThumbnail` + compressor: max 800×600 JPEG q0.8 at t≈0. */
const POSTER_MAX_WIDTH = 800;
const POSTER_MAX_HEIGHT = 600;
const POSTER_QUALITY = 0.8;
const POSTER_TIMEOUT_MS = 5000;

const cleanupVideo = (video, objectUrl) => {
  try {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    video.removeAttribute('src');
    video.load();
  } catch {
    // Best-effort; the element is discarded after capture.
  }
};

/**
 * Draw the first frame of a local video File to a JPEG. Returns null when the
 * file is not a video or the browser cannot decode a frame in time.
 *
 * @param {File} file
 * @returns {Promise<{ poster: File, imageSize: string }|null>}
 */
const captureVideoPoster = file => {
  if (typeof document === 'undefined' || !file?.type?.startsWith('video/')) {
    return Promise.resolve(null);
  }

  return new Promise(resolve => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;

    const timer = { id: null };
    const finish = result => {
      if (settled) return;
      settled = true;
      clearTimeout(timer.id);
      cleanupVideo(video, objectUrl);
      resolve(result);
    };

    timer.id = setTimeout(() => finish(null), POSTER_TIMEOUT_MS);

    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.addEventListener('error', () => finish(null));
    video.addEventListener('seeked', () => {
      try {
        const srcW = video.videoWidth;
        const srcH = video.videoHeight;
        if (!srcW || !srcH) {
          finish(null);
          return;
        }
        const scale = Math.min(1, POSTER_MAX_WIDTH / srcW, POSTER_MAX_HEIGHT / srcH);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(srcW * scale));
        canvas.height = Math.max(1, Math.round(srcH * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          finish(null);
          return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          blob => {
            if (!blob) {
              finish(null);
              return;
            }
            finish({
              poster: new File([blob], 'thumb.jpg', { type: 'image/jpeg' }),
              imageSize: `${srcW}x${srcH}`,
            });
          },
          'image/jpeg',
          POSTER_QUALITY,
        );
      } catch {
        finish(null);
      }
    });
    video.addEventListener('loadedmetadata', () => {
      try {
        video.currentTime = Math.min(0.001, video.duration || 0);
      } catch {
        finish(null);
      }
    });
    video.src = objectUrl;
  });
};

export default captureVideoPoster;
